import { eSearchFilterType, SearchFilterOperationModel, toLabeledField } from "operations/searches/search-filter-operation-model";
import { DRIVETIME_FILTER_KEYS, hasDriveTimeDestination, IHARSearchListingsFilters, ISearchListingsFilters, ToolFilterAmenities } from "types/filters/search-listings-filters";
import { eGarageDesc, eStories, ISearchListingsToolInput, IToolAmenities } from "types/inputs/search-listings-input";
import { categoriesMap, toolInputKeyForQuickAccess } from "utils/constants";
import { harAmenitiesOptions, mapStatusCodeToAvailabilityLabel, normalizeFinanceValue } from "utils/filtersData";
import { bindDetach, mapPropertyClassIdsToNames, mapPropTypeCodesToNames, splitIntoArray, switchCase, typedKeys } from "utils/helpers";


// This model needs to be in sync with the backend counterpart app/ai_search/operations/searches/search_listings_operation_model.py

// Geo-scoping filters HAR.com sends on a search URL that `SearchListingsToolInput`
// has no field for. They are modeled on `ISearchListingsFilters` (both ingress
// paths set them) and accepted by `SearchListingsServiceInput`, so the tool input
// carries them through `extra` — see `fromFiltersToToolInput`. `map_tools_*` are
// NOT in this list: those are declared tool fields and travel modeled.
const NATIVE_GEO_FILTER_KEYS = ["marketarea", "latitude", "longitude", "radius"] as const;
const DRIVETIME_REPLACED_NATIVE_GEO_KEYS = ["latitude", "longitude", "radius"] as const;

export class SearchListingsOperationModel extends SearchFilterOperationModel<IHARSearchListingsFilters, ISearchListingsFilters, ISearchListingsToolInput>
{
    private readonly TAG: string = "search-listings-filters";
    public DEFAULT_LIMIT: number = 10;

    public getSearchUserMessage(filters: ISearchListingsToolInput, type: eSearchFilterType): string
    {
        const pretext = type == eSearchFilterType.Search
            ? "Show me properties based on the following filters"
            : "Filter Properties By";
    
        return this.createComponentMessage(this.TAG, pretext);
    }

    public getNextPageUserMessage(filters: ISearchListingsToolInput): string
    {
        // `filters` is already advanced by normalizeFilters -> applyNextPage, so
        // report its current page (getPage), not the page after it (getNextPage).
        const { page } = this.getPage(filters.start, filters.limit);
        const pretext = `Page ${page} of`;
        return this.createComponentMessage(this.TAG, pretext);
    }
    
    protected convertNativeToFilters(_native: IHARSearchListingsFilters): ISearchListingsFilters.Raw
    {
        const native = { ..._native };
        const filters: ISearchListingsFilters.Raw = {};
        filters.extra = {};

        const detach = bindDetach(native);
        const detachNativeNum = bindDetach(native, this.nativeNum);
        const detachNativeBool = bindDetach(native, this.nativeBool);
        const detachSplitIntoArray = bindDetach(native, splitIntoArray<string>);

        // Detach a numeric native value and assign it only when present, so absent
        // keys don't leave `undefined` entries on the result. Present `0` is kept.
        // A non-numeric query value makes `nativeNum` yield NaN, which serializes
        // to `null` on the wire and is therefore never a real filter — drop it, so
        // it can't count as a "present" value downstream (a NaN map bound would
        // otherwise satisfy the filter-pill presence check and render a pill for a
        // bound the search never applied).
        const assignNum = (key: keyof IHARSearchListingsFilters): void => {
            const value = detachNativeNum(key as never);
            if (value !== undefined && Number.isFinite(value)) (filters as Record<string, unknown>)[key] = value;
        };

        // --- Location ---
        if (native.listing_agentid) filters.listing_agentid = detach("listing_agentid");
        //if (native.ptids) filters.extra.ptids = splitIntoArray(native.ptids).map(Number).join(",");
        if (native.nid) filters.nid = detachNativeNum("nid");
        if (native.community) filters.community = detachSplitIntoArray("community")?.map(Number);
        if (native.zip_code) filters.zip_code = detachSplitIntoArray("zip_code");
        if (native.subdivisions) filters.subdivisions = detach("subdivisions");
        if (native.streetaddress) filters.streetaddress = detach("streetaddress");
        if (native.fips_code) filters.fips_code = detachNativeNum("fips_code");
        if (native.city) filters.city = detach("city"); // transformApiFilters splits on ",".
        if (native.mlsnum) filters.mlsnum = detach("mlsnum");

        // --- Status / availability ---
        const status = detach("all_status");
        if (status) filters.all_status = status;
        const sold = detachNativeBool("sold");
        if (sold !== undefined) filters.sold = sold;
        const forSale = detachNativeNum("for_sale");
        if (forSale !== undefined) filters.for_sale = forSale;

        // --- Ranges / numerics ---
        assignNum("listing_price_min");
        assignNum("listing_price_max");
        assignNum("bedroom_min");
        assignNum("bedroom_max");
        assignNum("full_bath_min");
        assignNum("full_bath_max");
        assignNum("half_bath_num");
        assignNum("hoa_fee_max");
        assignNum("price_sqft_min");
        assignNum("price_sqft_max");
        assignNum("lotsize_min");
        assignNum("lotsize_max");
        assignNum("acres_min");
        assignNum("acres_max");
        assignNum("square_feet_min");
        assignNum("square_feet_max");
        assignNum("year_built_min");
        assignNum("year_built_max");
        assignNum("garage_num");
        assignNum("parking");

        // --- Enums / scalars ---
        if (native.style) filters.style = detach("style");
        // HAR.com sends `finance` in shapes that do not match `financeOptions`
        // byte-for-byte (percent-encoded apostrophe, no `Seller to ` prefix).
        // Canonicalize here so the pills, the Filters panel, the tool input and
        // the HAR call all carry the same string.
        if (native.finance) filters.finance = normalizeFinanceValue(detach("finance"));
        if (native.new_constr) filters.new_constr = detach("new_constr");
        if (native.garage_desc) filters.garage_desc = detach("garage_desc");
        if (native.stories) filters.stories = detachSplitIntoArray("stories");
        if (native.property_class_id)
            filters.property_class_id = detachSplitIntoArray("property_class_id");

        // HAR.com only sends `property_class_id` for FOR-SALE searches; for-rent
        // searches send `prop_type` with rental codes instead (see
        // share_url_util._build_params, which pairs `prop_type` with `for_sale=0`).
        // Normalize the codes to canonical NAMES and merge into the same field:
        // `property_class_id` already legitimately carries names on the UI-sourced
        // path (Filters.def.ts `fromUIToFilters`), and every consumer normalizes
        // with `mapPropertyClassIdsToNames`, which is idempotent on names. Left
        // undetached, `prop_type` would fall into `filters.extra` below — invisible
        // to the modal and the pills, and opaque to the LLM.

        // All-or-nothing on the key: if ANY code maps we detach `prop_type` and own
        // the property type through the modeled path, dropping any unmappable token
        // alongside it; if NONE maps we leave the key untouched so it stays in
        // `extra` and is replayed verbatim, exactly as before this branch existed.
        //
        // The alternative — keeping a partly-mappable value in `extra` — is worse,
        // not more conservative. `extra` is applied server-side only where the
        // modeled payload is None (search_listings_service), so the leftover copy
        // is inert while a property type is selected, then resurrects the moment
        // the user clears the checkboxes: their deselection would be silently
        // undone. No other `extra` key is both modeled and passed through, and this
        // is why.
        const propTypeNames = mapPropTypeCodesToNames(native.prop_type);
        if (propTypeNames?.length) {
            detach("prop_type");
            filters.property_class_id = Array.from(new Set([
                ...mapPropertyClassIdsToNames(filters.property_class_id) ?? [],
                ...propTypeNames,
            ]));
            // `prop_type` is a lease-context param (FilterContext.LEASE): HAR emits
            // it only for rentals, and search_listings_tool only produces it when
            // for_sale == 0. Since `for_sale` defaults to 1 downstream
            // (`for_sale = int(not for_rent)`), a rent URL that omits it would
            // otherwise run as a for-sale search. An explicit `for_sale` still wins;
            // it was already detached above, so `undefined` means absent from the URL.
            if (filters.for_sale === undefined) filters.for_sale = 0;
        }

        if (native.sort) filters.sort = detach("sort");

        // --- Geo-scoping (market area / radius search / map bounds / drawn polygon) ---
        // `marketarea`/`latitude`/`longitude`/`radius` are NATIVE_GEO_FILTER_KEYS:
        // detaching them here is safe because `fromFiltersToToolInput` writes the
        // modeled values back into `extra`, which is the only channel that carries
        // them to the search.
        if (native.marketarea) filters.marketarea = detach("marketarea");
        assignNum("latitude");
        assignNum("longitude");
        assignNum("radius");
        assignNum("map_tools_nwlat");
        assignNum("map_tools_nwlng");
        assignNum("map_tools_selat");
        assignNum("map_tools_selng");
        if (native.map_tools_polygon) filters.map_tools_polygon = detach("map_tools_polygon");

        // --- Drive time. Modeled (tool fields exist for all four), so they leave
        //     the passthrough bag and get their own pill and modal controls. ---
        for (const key of DRIVETIME_FILTER_KEYS) {
            const value = detach(key);
            if (value) filters[key] = value;
        }

        // --- Quick-access category flags: copied verbatim; transformApiFilters reads
        //     them via `categoriesMap` (incl. its `new_constr2 === "Yes"` guard). ---
        for (const key of typedKeys(categoriesMap)) {
            const value = detach(key);
            if (value) (filters as Record<string, unknown>)[key] = value;
        }

        // --- Schools ---
        if (native.school_district) filters.school_district = detach("school_district");
        if (native.schoolelementary) filters.schoolelementary = detach("schoolelementary");
        if (native.schoolmiddle) filters.schoolmiddle = detach("schoolmiddle");
        if (native.schoolhigh) filters.schoolhigh = detach("schoolhigh");
        if (native.schoolid) filters.schoolid = detachNativeNum("schoolid");

        // --- Amenities: collapse the individual native flags into the array shape
        const amenities = new Set<ToolFilterAmenities>();
        for (const { value } of harAmenitiesOptions) {
            if(value === "private_pool") { continue; } // handled separately below
            if(detach(value)) { amenities.add(value); }
        }

        const privatePool = detach("private_pool");
        if (privatePool != null && privatePool !== "") {
            amenities.add(privatePool === "1" ? "private_pool" : "no_private_pool");
        }

        if (amenities.size) filters.amenities = Array.from(amenities);

        // Drop 'page' filter, if present (exclusive of HAR.com UI)
        if('page' in filters) delete filters.page;

        filters.extra = native;
        return filters;
    }

    // Inverse of `convertNativeToFilters` on the tool side: rebuild the tool-call
    // input shape from the flat `restructure_filters` output.
    public fromFiltersToToolInput(filters: ISearchListingsFilters | ISearchListingsFilters.Raw): ISearchListingsToolInput
    {
        const toolInput: ISearchListingsToolInput = {};

        // --- Location (decode the URL-encoded native values) ---
        if (filters.streetaddress) toolInput.streetaddress = decodeURIComponent(filters.streetaddress);
        if (filters.city) toolInput.city = splitIntoArray(filters.city).map(decodeURIComponent);
        // A bare id on newer artifacts, a list on older ones (CB-1799).
        const community = [filters.community ?? []].flat();
        if (community.length) toolInput.community = toLabeledField(community);
        // Scalar, not an array: `validate_neighborhood_id` raises a ToolException
        // on a list, so a wrapped id got the whole search declined.
        if (filters.nid != null) toolInput.neighborhood_id = toLabeledField(filters.nid);
        else if (filters.neighborhood_id != null) toolInput.neighborhood_id = toLabeledField(filters.neighborhood_id);
        if (filters.zip_code?.length) toolInput.zip_code = filters.zip_code.map(decodeURIComponent);
        if (filters.subdivisions) toolInput.subdivisions = [decodeURIComponent(filters.subdivisions)];
        if (filters.fips_code != null) toolInput.county_id = toLabeledField(filters.fips_code);
        if (filters.mlsnum) toolInput.mls_number = filters.mlsnum;

        // --- Status / availability ---
        // The tool input's `availability` uses backend availabilityEnum labels
        // ("Active"/"WITH"), but `all_status` may carry MLS dropdown codes
        // ("A"/"with") from the UI path — convert each token to its label.
        if (filters.all_status) toolInput.availability = splitIntoArray(filters.all_status).map(mapStatusCodeToAvailabilityLabel);
        if (filters.sold !== undefined) toolInput.sold = filters.sold;
        if (filters.for_sale !== undefined) toolInput.for_rent = filters.for_sale === 1 ? 0 : 1;

        // --- Ranges: collapse the flat min/max pairs into `{ min, max }` ---
        const price = this.toRange(filters.listing_price_min, filters.listing_price_max);
        if (price) toolInput.price = price;
        const bedrooms = this.toRange(filters.bedroom_min, filters.bedroom_max);
        if (bedrooms) toolInput.bedrooms_beds = bedrooms;
        const baths = this.toRange(filters.full_bath_min, filters.full_bath_max);
        if (baths) toolInput.baths_bathrooms = baths;
        const priceSqft = this.toRange(filters.price_sqft_min, filters.price_sqft_max);
        if (priceSqft) toolInput.price_sqft = priceSqft;

        // --- Numerics / scalars ---
        if (filters.half_bath_num != null) toolInput.half_bath_num = filters.half_bath_num > 0;
        if (filters.hoa_fee_max != null) toolInput.hoa_fee_max = filters.hoa_fee_max;
        if (filters.lotsize_min != null) toolInput.lotsize_min = filters.lotsize_min;
        if (filters.lotsize_max != null) toolInput.lotsize_max = filters.lotsize_max;
        if (filters.acres_min != null) toolInput.acres_min = filters.acres_min;
        if (filters.acres_max != null) toolInput.acres_max = filters.acres_max;
        if (filters.square_feet_min != null) toolInput.square_feet_min = filters.square_feet_min;
        if (filters.square_feet_max != null) toolInput.square_feet_max = filters.square_feet_max;
        if (filters.year_built_min != null) toolInput.year_built_min = filters.year_built_min;
        if (filters.year_built_max != null) toolInput.year_built_max = filters.year_built_max;
        if (filters.garage_num != null) toolInput.garage_num = filters.garage_num;
        if (filters.parking != null) toolInput.parking = filters.parking;

        // --- Enums / scalars ---
        if (filters.garage_desc) toolInput.garage_desc = filters.garage_desc as eGarageDesc;
        if (filters.stories?.length) toolInput.stories = filters.stories as eStories[];
        if (filters.new_constr) toolInput.new_constr = filters.new_constr;
        if (filters.style) toolInput.style = filters.style;
        if (filters.finance) toolInput.finance = filters.finance;
        // `property_type` on the tool input is a list of NAMES (the backend maps
        // them to ids); `property_class_id` may hold ids (native-sourced) or names
        // (UI-sourced), so normalize to names before sending.
        if (filters.property_class_id?.length) toolInput.property_type = mapPropertyClassIdsToNames(filters.property_class_id);
        if (filters.sort) toolInput.sort = filters.sort;

        // Pagination offset: carried through so a next-page operation continues
        if (filters.start != null) toolInput.start = filters.start;
        if (filters.max != null) toolInput.limit = filters.max;

        // --- Quick-access category flags -> individual boolean fields ---
        for (const key of typedKeys(categoriesMap)) {
            const value = (filters as Record<string, unknown>)[key];
            if (!value) continue;
            // Mirror toUIFilters' guard: new_constr2 is a string flag that only
            // counts when it equals "Yes" (a non-"Yes" truthy value must not
            // select the category).
            if (key === "new_constr2" && value !== "Yes") continue;
            // `categoriesMap` values are UI category names; the tool input names
            // `Foreclosure` after HAR's flag (`forcl`). Go through the mapping so
            // the key survives the backend's tool-field whitelist.
            (toolInput as Record<string, boolean>)[toolInputKeyForQuickAccess(categoriesMap[key])] = true;
        }
        if (filters.listed_today) toolInput.listed_today = true;

        // --- Amenities: expand the array back into individual boolean flags ---
        if (filters.amenities?.length) {
            for (const amenity of filters.amenities) {
                if (amenity === "private_pool") toolInput.pool = true;
                else if (amenity === "no_private_pool") toolInput.pool = false;
                else (toolInput as IToolAmenities)[amenity] = true;
            }
        }

        // --- Schools ---
        if (filters.school_district) toolInput.school_district = toLabeledField(filters.school_district);
        if (filters.schoolelementary) toolInput.elemantary_school_id = toLabeledField(filters.schoolelementary);
        if (filters.schoolmiddle) toolInput.middle_school_id = toLabeledField(filters.schoolmiddle);
        if (filters.schoolhigh) toolInput.high_school_id = toLabeledField(filters.schoolhigh);
        if (filters.schoolid != null) toolInput.school_id = toLabeledField(filters.schoolid, String);

        // --- Agent ---
        if (filters.listing_agentid) toolInput.listing_agentid = toLabeledField(filters.listing_agentid);

        // --- Geo (map-drawn bounds / polygon) vs. drive time. This is the funnel
        //     the modal and the URL seed both go through, so the two location
        //     modes are reconciled here once. Drive time is its own mode on
        //     har.com: the site clears the drawn shape and the map bounds when a
        //     drive-time input changes, and the server discards them anyway.
        //     Dropping them here keeps the persisted tool input (which the pills
        //     and the reopened modal read) honest: no "Shape" or "Map Bounds"
        //     pill outlives the scope, and clearing the destination later cannot
        //     resurrect it. ---
        const driveTime = hasDriveTimeDestination(filters);
        if (!driveTime) {
            if (filters.map_tools_nwlat != null) toolInput.map_tools_nwlat = filters.map_tools_nwlat;
            if (filters.map_tools_nwlng != null) toolInput.map_tools_nwlng = filters.map_tools_nwlng;
            if (filters.map_tools_selat != null) toolInput.map_tools_selat = filters.map_tools_selat;
            if (filters.map_tools_selng != null) toolInput.map_tools_selng = filters.map_tools_selng;
            if (filters.map_tools_polygon) toolInput.map_tools_polygon = filters.map_tools_polygon;
        }

        // --- Drive time. har.com's form always submits the two times, so a URL
        //     whose destination was never resolved carries them alone, and the
        //     backend rejects a drive-time search without a destination. ---
        if (driveTime) {
            const address = filters.drivetime_address?.trim();
            if (address) toolInput.drivetime_address = address;
            if (filters.drivetime_center) toolInput.drivetime_center = filters.drivetime_center;
            if (filters.drivetime_arrival_time) toolInput.drivetime_arrival_time = filters.drivetime_arrival_time;
            // A malformed URL value would otherwise travel as NaN (null on the
            // wire, the string "NaN" back in the modal) or as 0, which the
            // backend refuses; the backend's default applies instead, as for
            // the bounds above. A value past har.com's longest option is left
            // to the backend, whose corrective message names the limit.
            const travel = Number(filters.drivetime_travel_time);
            if (Number.isFinite(travel) && travel > 0) {
                toolInput.drivetime_travel_time = travel;
            }
        }

        // --- Native-only geo scoping -> `extra` ---
        // `marketarea`/`latitude`/`longitude`/`radius` restrict the search to a
        // market area or to a radius around a point, but `SearchListingsToolInput`
        // declares no field for any of them: written at the top level they would be
        // dropped by the backend operation model, which keeps only the tool's own
        // fields (`_known_filters` in search_listings_operation_model.py). `extra` is
        // the one channel that reaches them — it is hashed into `base_filters_id`,
        // stored in tool state, and applied by `search_listings_service` onto the
        // matching `SearchListingsServiceInput` fields. Dropping them here made a
        // market-area or radius search run unscoped over the whole MLS.
        //
        // A modeled value overrides the copy `extra` may already hold, so a market
        // area changed in the filters modal is the one that runs. A key only `extra`
        // carries is left alone: `fromToolInputToUIFilters` restores `extra` without
        // re-modeling these four, so stripping them would drop the scoping of a
        // search refined from the artifact's filters panel.
        const extra: Record<string, string> = { ...filters.extra };
        for (const key of NATIVE_GEO_FILTER_KEYS) {
            const value = filters[key];
            if (value != null && value !== "") extra[key] = String(value);
        }
        // A point-radius scope is replaced by drive time, as the map scope is
        // above (the backend drops it too); `marketarea` is a region filter like
        // a city and stays.
        if (driveTime) {
            for (const key of DRIVETIME_REPLACED_NATIVE_GEO_KEYS) delete extra[key];
        }
        if (Object.keys(extra).length) toolInput.extra = extra;
        return toolInput;
    }

    protected applyNextPage(filters: ISearchListingsToolInput): ISearchListingsToolInput
    {
        const { start } = this.getNextPage(filters.start, filters.limit);
        return { ...filters, start };
    }

    // Collapse a flat min/max pair into a `{ min, max }` range, omitting absent
    // bounds. Returns `undefined` when neither bound is present.
    private toRange(min?: number, max?: number): Record<string, number> | undefined
    {
        if (min == null && max == null) return undefined;
        const range: Record<string, number> = {};
        if (min != null) range.min = min;
        if (max != null) range.max = max;
        return range;
    }

    // Coerce a query-string value to a number; empty/missing yields `undefined`.
    private nativeNum(v: string | undefined): number | undefined
    {
        return v != null && v !== "" ? Number(v) : undefined;
    }

    // Coerce a query-string flag (e.g. "1" / "true" / "yes") to a boolean, or
    // `undefined` when the key is absent.
    private nativeBool(v: string | undefined): boolean | undefined
    {
        return v != null && v !== "" ? /^(1|true|yes)$/i.test(v) : undefined;
    }
}